/**
 * supabase/transfers.js — Transferencia de prospectos entre asesoras
 * (lead_transfers: crear/aceptar/rechazar/consultar pendientes).
 * Parte de la fachada src/lib/supabase.js.
 */

import { getSupabase } from "./client";
import { updateLead, logLeadActivity } from "./leads";

// ═══════════════════════════════════════════════════
// LEAD TRANSFERS — Transferencia de prospecto entre asesoras
// ═══════════════════════════════════════════════════

/**
 * Crea una solicitud de transferencia (status=pending).
 * El lead NO cambia de asesor hasta que la receptora acepta.
 */
export async function createTransfer({ leadId, fromAsesorId, toAsesorId, motivo }) {
  if (!leadId || !fromAsesorId || !toAsesorId) {
    return { data: null, error: { message: "Faltan campos requeridos" } };
  }
  if (fromAsesorId === toAsesorId) {
    return { data: null, error: { message: "No puedes transferirte a ti misma" } };
  }
  const client = getSupabase();

  const { data: existing } = await client
    .from("lead_transfers")
    .select("id")
    .eq("lead_id", leadId)
    .eq("status", "pending")
    .maybeSingle();
  if (existing) {
    return { data: null, error: { message: "Ya hay una transferencia pendiente para este prospecto" } };
  }

  const { data, error } = await client
    .from("lead_transfers")
    .insert({
      lead_id: leadId,
      from_asesor_id: fromAsesorId,
      to_asesor_id: toAsesorId,
      motivo: motivo || null,
      status: "pending",
    })
    .select()
    .single();

  if (!error && data) {
    await logLeadActivity(leadId, {
      tipo: "transfer_requested",
      titulo: "Transferencia solicitada",
      descripcion: motivo || "Sin motivo especificado",
      asesor_id: fromAsesorId,
      metadata: { transfer_id: data.id, to_asesor_id: toAsesorId },
    });
  }
  return { data, error };
}

/**
 * Acepta una transferencia. Cambia el asesor_id del lead.
 * NO cambia la instancia (el wa sigue llegando por el mismo número).
 */
export async function acceptTransfer(transferId, acceptingAsesorId) {
  if (!transferId || !acceptingAsesorId) {
    return { data: null, error: { message: "Parámetros faltantes" } };
  }
  const client = getSupabase();

  const { data: transfer, error: tErr } = await client
    .from("lead_transfers")
    .select("*")
    .eq("id", transferId)
    .single();
  if (tErr || !transfer) {
    return { data: null, error: tErr || { message: "Transferencia no encontrada" } };
  }
  if (transfer.status !== "pending") {
    return { data: null, error: { message: `Esta transferencia ya está ${transfer.status}` } };
  }
  if (transfer.to_asesor_id !== acceptingAsesorId) {
    return { data: null, error: { message: "No tienes permiso para aceptar esta transferencia" } };
  }

  const { error: leadErr } = await updateLead(transfer.lead_id, { asesor_id: transfer.to_asesor_id });
  if (leadErr) return { data: null, error: leadErr };

  const { data, error } = await client
    .from("lead_transfers")
    .update({ status: "accepted", resolved_at: new Date().toISOString() })
    .eq("id", transferId)
    .select()
    .single();

  await logLeadActivity(transfer.lead_id, {
    tipo: "transfer_accepted",
    titulo: "Transferencia aceptada",
    descripcion: "Prospecto transferido a nueva asesora",
    asesor_id: acceptingAsesorId,
    metadata: { transfer_id: transferId, from_asesor_id: transfer.from_asesor_id },
  });

  return { data, error };
}

/** Rechaza una transferencia. El lead se queda con la asesora original. */
export async function rejectTransfer(transferId, rejectingAsesorId, reason = "") {
  if (!transferId || !rejectingAsesorId) {
    return { data: null, error: { message: "Parámetros faltantes" } };
  }
  const client = getSupabase();

  const { data: transfer, error: tErr } = await client
    .from("lead_transfers")
    .select("*")
    .eq("id", transferId)
    .single();
  if (tErr || !transfer) {
    return { data: null, error: tErr || { message: "Transferencia no encontrada" } };
  }
  if (transfer.status !== "pending") {
    return { data: null, error: { message: `Esta transferencia ya está ${transfer.status}` } };
  }
  if (transfer.to_asesor_id !== rejectingAsesorId) {
    return { data: null, error: { message: "No tienes permiso para rechazar esta transferencia" } };
  }

  const { data, error } = await client
    .from("lead_transfers")
    .update({ status: "rejected", resolved_at: new Date().toISOString() })
    .eq("id", transferId)
    .select()
    .single();

  await logLeadActivity(transfer.lead_id, {
    tipo: "transfer_rejected",
    titulo: "Transferencia rechazada",
    descripcion: reason || "Sin razón especificada",
    asesor_id: rejectingAsesorId,
    metadata: { transfer_id: transferId, from_asesor_id: transfer.from_asesor_id },
  });

  return { data, error };
}

/**
 * Obtiene transferencias pendientes dirigidas a una asesora.
 * Incluye datos del lead y de la asesora origen para el modal.
 */
export async function getPendingTransfersFor(asesorId) {
  if (!asesorId) return [];
  const { data, error } = await getSupabase()
    .from("lead_transfers")
    .select(`
      id, motivo, created_at, status,
      lead:lead_id(id, nombre_tutor, nombre_alumno, nivel_educativo, grado_especifico, telefono, wa_jid, calidad_lead, tema_consulta),
      from_asesor:from_asesor_id(id, nombre, email)
    `)
    .eq("to_asesor_id", asesorId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });
  if (error) {
    console.warn("[supabase] getPendingTransfersFor error:", error.message);
    return [];
  }
  return data || [];
}

/** Devuelve la transferencia pendiente activa de un lead, si existe. */
export async function getPendingTransferForLead(leadId) {
  if (!leadId) return null;
  const { data } = await getSupabase()
    .from("lead_transfers")
    .select(`id, motivo, created_at, from_asesor:from_asesor_id(nombre), to_asesor:to_asesor_id(nombre)`)
    .eq("lead_id", leadId)
    .eq("status", "pending")
    .maybeSingle();
  return data || null;
}
